import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { AuthSplitLayout } from '../../components/auth/AuthSplitLayout';
import { Alert } from '../../components/ui/Alert';
import { forgotPassword } from '../../modules/platform/api/auth';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { ROUTES } from '../../utils/constants';
import { validateEmail } from '../../utils/validation';

interface ForgotPasswordFormData {
  email: string;
}

export const ForgotPasswordPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [touchedEmail, setTouchedEmail] = useState(false);

  const {
    register: registerField,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const emailRegister = registerField('email', {
    required: 'Email is required',
    validate: (value) => validateEmail(value) || 'Invalid email address',
  });

  const showEmailError = (submitted || touchedEmail) && errors.email?.message;

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setSubmitted(true);
    setIsLoading(true);
    try {
      await forgotPassword({ email: data.email });
      setSuccess(true);
      showSuccessToast('Password reset email sent!');
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <AuthSplitLayout
        title="Forgot Password"
        subtitle="Enter your email to receive a password reset link"
      >
        <div className="space-y-4">
          <Alert variant="success">
            Password reset email sent! Please check your inbox.
          </Alert>
          <Link to={ROUTES.LOGIN}>
            <Button className="w-full">Back to Login</Button>
          </Link>
        </div>
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout
      title="Forgot Password"
      subtitle="Enter your email to receive a password reset link"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Input
          label="Email"
          type="email"
          {...emailRegister}
          onBlur={(e) => {
            emailRegister.onBlur(e);
            setTouchedEmail(true);
          }}
          error={showEmailError ? errors.email?.message : undefined}
          placeholder="you@example.com"
        />

        <Button type="submit" className="w-full" isLoading={isLoading}>
          Send Reset Link
        </Button>

        <p className="text-center text-sm text-gray-600 dark:text-gray-400">
          Remember your password?{' '}
          <Link
            to={ROUTES.LOGIN}
            className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            Sign in
          </Link>
        </p>
      </form>
    </AuthSplitLayout>
  );
};
